import { useEffect, useMemo, useState } from "react";
import { useLiveRoomSocket } from "@/hooks/useLiveRoomWS";
import { Target } from "lucide-react";

/**
 * Live-stream goal progress bar. Polls GET /live/{streamId} every 4s to pick up
 * gift-driven goal_progress increments. Hides itself if no goal is configured.
 */
export default function LiveGoalBar({ streamId, initial, variant }) {
  const [stream, setStream] = useState(initial || null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (variant !== "chip" || !expanded) return;
    const t = setTimeout(() => setExpanded(false), 6000);
    return () => clearTimeout(t);
  }, [variant, expanded]);

  // Goal progress ticks arrive on `gift:sent`; `sync` re-baselines after a
  // reconnect; `initial` (the room's live doc) seeds it. No polling.
  const socket = useLiveRoomSocket();
  useEffect(() => { if (initial) setStream((cur) => ({ ...(cur || {}), ...initial })); }, [initial]);
  useEffect(() => {
    const offs = [
      socket.subscribe("gift:sent", (m) => {
        if (typeof m?.goal_progress !== "number") return;
        setStream((cur) => ({ ...(cur || {}), goal_progress: m.goal_progress, goal_coins: m.goal_coins ?? cur?.goal_coins }));
      }),
      socket.subscribe("sync", (s) => { if (s?.stream) setStream((cur) => ({ ...(cur || {}), ...s.stream })); }),
      socket.subscribe("live:update", (m) => { if (m?.stream) setStream((cur) => ({ ...(cur || {}), ...m.stream })); }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket]);

  const goal = stream?.goal_coins || 0;
  const progress = stream?.goal_progress || 0;
  const pct = useMemo(() => {
    if (!goal || goal <= 0) return 0;
    return Math.min(100, Math.round((progress / goal) * 100));
  }, [goal, progress]);

  if (!goal) return null;

  if (variant === "chip") {
    return (
      <button
        type="button"
        data-testid="live-goal-chip"
        onClick={() => setExpanded((e) => !e)}
        className="pointer-events-auto flex items-center gap-1.5 h-8 bg-black/45 backdrop-blur-md border border-white/10 rounded-full px-2 text-white text-xs transition-all"
        title="Stream goal"
        aria-expanded={expanded}
      >
        <span className="w-4 h-4 rounded-full bg-yellow-400 text-black flex items-center justify-center shrink-0">
          <Target size={10} />
        </span>
        {expanded && (
          <>
            <span className="w-14 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <span
                className="block h-full bg-gradient-to-r from-yellow-400 to-rose-500 transition-all duration-700"
                style={{ width: `${pct}%` }}
                data-testid="live-goal-progress"
              />
            </span>
            <span className="text-yellow-300 tabular-nums text-[10px] font-semibold" data-testid="live-goal-values">
              {progress.toLocaleString()}/{goal.toLocaleString()}
            </span>
          </>
        )}
        <span className="text-yellow-300 text-[10px] font-bold tabular-nums">{pct}%</span>
      </button>
    );
  }

  return (
    <div
      data-testid="live-goal-bar"
      className="pointer-events-none absolute top-24 sm:top-28 left-4 right-4 z-20 flex justify-center"
    >
      <div className="w-full max-w-md bg-black/55 backdrop-blur-md border border-white/10 rounded-full pl-3 pr-4 py-2 shadow-lg">
        <div className="flex items-center gap-2 text-white">
          <div className="w-7 h-7 rounded-full bg-yellow-400 text-black flex items-center justify-center shrink-0">
            <Target size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2 text-[11px] uppercase tracking-widest">
              <span className="text-white/70">Stream goal</span>
              <span className="text-yellow-300 font-semibold" data-testid="live-goal-values">
                {progress.toLocaleString()} / {goal.toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 mt-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-yellow-400 to-rose-500 transition-all duration-700"
                style={{ width: `${pct}%` }}
                data-testid="live-goal-progress"
              />
            </div>
          </div>
          <div className="text-yellow-300 text-xs font-bold w-9 text-right">{pct}%</div>
        </div>
      </div>
    </div>
  );
}
